// Centralized logging utility with module-based debug flags
// Factory function following project architecture rules

export type LogLevel = "error" | "warn" | "info" | "debug" | "trace";
export type LogModule =
  | "socket"
  | "stateSync"
  | "ui"
  | "stage"
  | "persistence"
  | "adapters"
  | "transform"
  | "helpers"
  | "sketch"
  | "selection";

interface LoggerConfig {
  readonly enabled?: Partial<Record<LogModule, boolean>>;
  readonly level?: LogLevel;
}

interface LoggerAPI {
  readonly error: (module: LogModule, message: string, ...args: any[]) => void;
  readonly warn: (module: LogModule, message: string, ...args: any[]) => void;
  readonly info: (module: LogModule, message: string, ...args: any[]) => void;
  readonly debug: (module: LogModule, message: string, ...args: any[]) => void;
  readonly trace: (module: LogModule, message: string, ...args: any[]) => void;
  readonly isEnabled: (module: LogModule, level?: LogLevel) => boolean;
  readonly enable: (module: LogModule) => void;
  readonly disable: (module: LogModule) => void;
  readonly setLevel: (level: LogLevel) => void;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3,
  trace: 4,
};

/**
 * Creates a centralized logger with module-based debug flags
 *
 * @param config - Logger configuration
 * @returns Logger API
 *
 * @example
 * ```typescript
 * const logger = createLogger({
 *   enabled: { socket: true, stateSync: true },
 *   level: 'debug'
 * });
 *
 * logger.debug('socket', '📤 sending delta', data);
 * logger.info('stateSync', 'hydrating from session');
 * ```
 */
export const createLogger = (config: LoggerConfig = {}): LoggerAPI => {
  const { level = "info" } = config;

  // Read enabled modules from localStorage or config
  const readEnabledModules = (): Set<LogModule> => {
    const enabled = new Set<LogModule>();

    // Start with config
    if (config.enabled) {
      for (const [mod, isEnabled] of Object.entries(config.enabled)) {
        if (isEnabled) enabled.add(mod as LogModule);
      }
    }

    // Override with localStorage
    try {
      const stored =
        localStorage.getItem("debug") || sessionStorage.getItem("debug");
      if (stored) {
        const modules = stored.split(",").map((s) => s.trim());
        if (modules.includes("*")) {
          // Enable all
          const allModules: LogModule[] = [
            "socket",
            "stateSync",
            "ui",
            "stage",
            "persistence",
            "adapters",
            "transform",
            "helpers",
            "sketch",
            "selection",
          ];
          allModules.forEach((m) => enabled.add(m));
        } else {
          modules.forEach((m) => enabled.add(m as LogModule));
        }
      }
    } catch {}

    return enabled;
  };

  let enabledModules = readEnabledModules();
  let currentLevel: LogLevel = level;

  const shouldLog = (module: LogModule, logLevel: LogLevel): boolean => {
    // Errors and warnings always log
    if (logLevel === "error" || logLevel === "warn") return true;

    // Check if module is enabled
    if (!enabledModules.has(module)) return false;

    // Check log level
    return LOG_LEVELS[logLevel] <= LOG_LEVELS[currentLevel];
  };

  const log = (
    logLevel: LogLevel,
    module: LogModule,
    message: string,
    ...args: any[]
  ): void => {
    if (!shouldLog(module, logLevel)) return;

    const prefix = `[${module}]`;
    const consoleFn = logLevel === "error" || logLevel === "warn" 
      ? console[logLevel] 
      : console.log;

    try {
      if (args.length > 0) {
        consoleFn(prefix, message, ...args);
      } else {
        consoleFn(prefix, message);
      }
    } catch {}
  };

  return {
    error: (module, message, ...args) => log("error", module, message, ...args),
    warn: (module, message, ...args) => log("warn", module, message, ...args),
    info: (module, message, ...args) => log("info", module, message, ...args),
    debug: (module, message, ...args) => log("debug", module, message, ...args),
    trace: (module, message, ...args) => log("trace", module, message, ...args),

    isEnabled: (module, level = currentLevel) =>
      shouldLog(module, level),

    enable: (module) => {
      enabledModules.add(module);
    },

    disable: (module) => {
      enabledModules.delete(module);
    },

    setLevel: (level) => {
      currentLevel = level;
    },
  };
};

// Create default global logger instance
export const logger = createLogger({
  enabled: {
    socket: true,
    stateSync: true,
    // Others disabled by default
  },
  level: "debug",
});
